import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import { HTMLAttributes, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

import { Button } from '@/components/custom/button'
import { PasswordInput } from '@/components/custom/password-input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

import { useAuth } from '@/contexts/auth-context'
import authService from '@/services/auth.service'
import { ErrorResponse } from '@/utils/axios-error-handle'

import { UserProfile } from '@/types/user.type'

type ResetPasswordFormProps = HTMLAttributes<HTMLDivElement> & {
  buttonClassName?: string
}

const formSchema = z
  .object({
    password: z
      .string()
      .min(1, {
        message: 'Please enter your password',
      })
      .min(6, {
        message: 'Password must be at least 6 characters long',
      }),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  })

export default function ResetPasswordForm({ className, buttonClassName, ...props }: ResetPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false)

  const router = useRouter()
  const token = router.isReady && typeof router.query.token === 'string' ? router.query.token : undefined
  const { toast } = useToast()
  const { login } = useAuth()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!token) {
      return
    }
    setIsLoading(true)
    try {
      const responseData = await authService.resetPassword({
        token,
        newPassword: data.password,
      })
      if (responseData.success) {
        toast({
          variant: 'default',
          title: `Password set successfully !`,
        })
        const loginResponse = await authService.login<UserProfile>({
          email: responseData.email,
          password: data.password,
        })
        const userSignInResponse = loginResponse.data
        const sessionId = loginResponse.sessionId

        if (!userSignInResponse) return

        const { isActive } = userSignInResponse

        if (!isActive) {
          localStorage.setItem('pendingVerifyEmail', responseData.email.toLowerCase())
          router.push('/auth/verify-account')
          return
        }

        login(userSignInResponse, sessionId, {
          accessToken: loginResponse.accessToken,
          refreshToken: loginResponse.refreshToken,
          accessTokenExpiresIn: loginResponse.accessTokenExpiresIn,
          refreshTokenExpiresIn: loginResponse.refreshTokenExpiresIn,
        })
        const redirectPath = responseData.isRedirectToCourse ? '/my-courses' : '/'
        router.push(redirectPath)
      }
    } catch (error) {
      const _error = error as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to reset password',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className={cn('mt-2', buttonClassName)} loading={isLoading}>
              Reset Password
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
